import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Translation, TranslocoLoader } from '@ngneat/transloco';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { APP_I18N_NAMESPACE } from '../providers/app-namespace.token';
import { deepMerge } from '../utils/deep-merge.util';

/**
 * Custom Transloco loader that merges base translations with app-specific overrides.
 *
 * Loading order:
 * 1. Base translations from libs/zop-i18n/assets/i18n/{lang}.json
 * 2. App overrides from apps/{app}/src/assets/i18n/overrides/{lang}.json
 * 3. Deep merge: overrides take precedence over base
 *
 * @example
 * // If INFLEWZ overrides "settings.groups.production" to remove it:
 * // Base: { "settings": { "groups": { "production": "Production" } } }
 * // Override: { "settings": { "groups": { "production": null } } }
 * // Result: "production" key removed from final translation
 */
@Injectable({ providedIn: 'root' })
export class TranslocoMergeLoader implements TranslocoLoader {
  private readonly http = inject(HttpClient);
  private readonly appNamespace = inject(APP_I18N_NAMESPACE);

  /**
   * Loads and merges translations for the specified language
   *
   * @param lang - Language code (e.g., 'en', 'de', 'kn')
   * @returns Observable of merged translation object
   */
  getTranslation(lang: string): Observable<Translation> {
    // Base translations are ALWAYS loaded (from shared zop-i18n library)
    const base$ = this.http
      .get<Translation>(`./assets/i18n/${lang}.json`)
      .pipe(
        catchError((error) => {
          console.warn(
            `Failed to load base translations for language: ${lang}`,
            error
          );
          return of({} as Translation);
        })
      );

    // App overrides are conditionally loaded
    // Zopup: no overrides (appNamespace = 'zopup', no overrides folder created)
    // INFLEWZ/Verified: has overrides
    const overrides$ = this.loadOverrides(lang);

    // Merge base + overrides using RxJS
    return forkJoin([base$, overrides$]).pipe(
      map(([base, overrides]) => {
        // Deep merge: overrides take precedence
        const merged = deepMerge(base, overrides);

        if (Object.keys(overrides).length > 0) {
          console.log(
            `[TranslocoMergeLoader] Merged ${lang}: ` +
              `${Object.keys(base).length} base keys + ` +
              `${Object.keys(overrides).length} override keys`
          );
        }

        return merged;
      })
    );
  }

  /**
   * Loads app-specific translation overrides
   * Returns empty object if no overrides exist
   */
  private loadOverrides(lang: string): Observable<Translation> {
    // Zopup has no overrides - skip HTTP request
    if (this.appNamespace === 'zopup') {
      return of({} as Translation);
    }

    // Attempt to load overrides for other apps
    return this.http
      .get<Translation>(`./assets/i18n/overrides/${lang}.json`)
      .pipe(
        catchError((error) => {
          // Overrides file not found is OK (not all apps need overrides)
          if (error.status === 404) {
            console.log(
              `[TranslocoMergeLoader] No overrides found for ${lang} in ${this.appNamespace}`
            );
          } else {
            console.warn(
              `Failed to load overrides for ${lang} in ${this.appNamespace}`,
              error
            );
          }
          return of({} as Translation);
        })
      );
  }
}
